import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { uploadAttachment } from '../../api/client';
import type { Attachment } from '../../api/types';
import { Modal } from '../../components/Modal';
import { avatarKey, boardAvatarApi, type BoardKind } from './boardAvatarApi';

export function BoardAvatarButton({ kind, id, title }: { kind: BoardKind; id: number; title: string }) {
  const [open, setOpen] = useState(false);
  return <><button className="board-avatar-button" type="button" title="Добавить или изменить аватарку" aria-label={`Изменить аватарку: ${title}`} onClick={() => setOpen(true)}><ImagePlus size={21}/></button>
    {open ? createPortal(<AvatarEditor key={`${kind}:${id}`} kind={kind} id={id} title={title} onClose={() => setOpen(false)}/>, document.body) : null}</>;
}

function AvatarEditor({ kind, id, title, onClose }: { kind: BoardKind; id: number; title: string; onClose: () => void }) {
  const client = useQueryClient(), input = useRef<HTMLInputElement>(null);
  const avatar = useQuery({ queryKey: avatarKey(kind, id), queryFn: () => boardAvatarApi.get(kind, id) });
  const [file, setFile] = useState<File | null>(null), [preview, setPreview] = useState(''), [error, setError] = useState(''), [progress, setProgress] = useState(0);
  const uploaded = useRef<{ file: File; attachment: Attachment } | null>(null);
  const saving = useRef(false);
  const save = useMutation({
    mutationFn: async () => {
      if (!file || !avatar.data?.canEdit) throw new Error('Не удалось загрузить данные аватарки.');
      saving.current = true;
      try {
        const attachment = uploaded.current?.file === file ? uploaded.current.attachment : await uploadAttachment(file, {}, setProgress);
        uploaded.current = { file, attachment }; // Retry an uncertain save without uploading again.
        return await boardAvatarApi.save(kind, id, attachment.id, avatar.data.attachmentId);
      } finally { saving.current = false; }
    },
    onSuccess: result => {
      client.setQueryData(avatarKey(kind, id), result);
      for (const queryKey of [['asset', id], ['assets'], ['states'], ['project-card', id], ['project-cards']]) void client.invalidateQueries({ queryKey });
      onClose();
    },
  });
  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const url = URL.createObjectURL(file); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const choose = (next?: File) => {
    if (!next || saving.current) return;
    if (!/\.(png|jpe?g|webp|gif)$/i.test(next.name)) { setError('Выберите картинку JPG, PNG, WebP или GIF.'); return; }
    if (!next.size || next.size > 20 * 1024 * 1024) { setError('Изображение должно быть не больше 20 МБ.'); return; }
    setFile(next); setError(''); setProgress(0); uploaded.current = null; save.reset();
  };
  const close = () => { if (!saving.current) onClose(); };
  return <Modal title={`Аватарка · ${title}`} onClose={close} size="small" className="board-avatar-editor">
    <div className="board-avatar-editor__body" onPaste={event => { const image = Array.from(event.clipboardData.files).find(item => item.type.startsWith('image/')); if (image) { event.preventDefault(); choose(image); } }}>
      <button type="button" className="board-avatar-editor__picture" disabled={save.isPending || !avatar.data?.canEdit} onClick={() => input.current?.click()} aria-label="Выбрать изображение">
        {preview || avatar.data?.preview ? <img src={preview || avatar.data?.original || avatar.data?.preview} alt="Аватарка"/> : <ImagePlus size={48}/>}
      </button>
      <input ref={input} type="file" hidden accept="image/jpeg,image/png,image/webp,image/gif" onChange={event => { choose(event.target.files?.[0]); event.target.value = ''; }}/>
      <button className="button button--secondary" type="button" disabled={save.isPending || !avatar.data?.canEdit} onClick={() => input.current?.click()}>Выбрать изображение</button>
      <small>JPG, PNG, WebP или GIF · до 20 МБ<br/>Можно вставить картинку из буфера обмена.</small>
      {avatar.isPending ? <p role="status">Загружаем аватарку…</p> : null}
      {avatar.error ? <p role="alert">{avatar.error.message} <button type="button" onClick={() => void avatar.refetch()}>Повторить</button></p> : null}
      {error || save.error ? <p className="form-error" role="alert">{error || save.error?.message}{save.error && <button type="button" onClick={() => { save.reset(); void avatar.refetch(); }}>Обновить</button>}</p> : null}
      <button className="button button--primary" type="button" disabled={!file || !avatar.data?.canEdit || save.isPending || Boolean(error)} onClick={() => save.mutate()}>
        {save.isPending ? <><LoaderCircle className="spin" size={17}/> {progress < 1 ? `Загрузка ${Math.round(progress * 100)}%` : 'Сохраняем…'}</> : 'Сохранить'}
      </button>
    </div>
  </Modal>;
}
